import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, STAFF_ROLES } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";
import { Logo } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Login() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      navigate(STAFF_ROLES.includes(data.role) ? "/staff" : "/dashboard");
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-obsidian flex flex-col" data-testid="login-page">
      <div className="hazard-stripes h-2" />
      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <div className="mb-8 text-center">
            <Link to="/" className="inline-block" data-testid="login-home-link"><Logo /></Link>
            <h1 className="font-heading font-extrabold uppercase tracking-tight text-charcoal text-2xl mt-6" data-testid="login-heading">
              Academy Login
            </h1>
            <p className="text-sm text-slate-500 mt-1">Applicants, trainees, operators and staff — one login.</p>
          </div>
          <form onSubmit={submit} className="bg-slate-panel border border-steel rounded-sm p-6 sm:p-8 space-y-5" data-testid="login-form">
            {error && (
              <div className="bg-red-500/10 border border-red-500/40 text-red-400 text-sm rounded-sm px-4 py-3" data-testid="login-error">
                {error}
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-600">Email</Label>
              <Input id="email" type="email" required autoComplete="email" value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-obsidian border-steel text-charcoal focus-visible:ring-lomond h-12 text-base"
                data-testid="login-email-input" placeholder="you@example.com" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-600">Password</Label>
              <Input id="password" type="password" required autoComplete="current-password" value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="bg-obsidian border-steel text-charcoal focus-visible:ring-lomond h-12 text-base"
                data-testid="login-password-input" placeholder="Your password" />
            </div>
            <Button type="submit" disabled={loading}
              className="w-full bg-lomond text-black font-heading font-extrabold uppercase tracking-wide hover:bg-[#E6B400] h-12 text-base"
              data-testid="login-submit-btn">
              {loading ? "Signing in..." : "Sign In"}
            </Button>
            <div className="flex items-center justify-between text-sm">
              <Link to="/forgot-password" className="text-slate-500 hover:text-lomond" data-testid="forgot-password-link">
                Forgot password?
              </Link>
              <Link to="/register" className="text-lomond hover:underline font-semibold" data-testid="register-link">
                Register interest
              </Link>
            </div>
          </form>
          <p className="text-center text-xs text-slate-500 mt-6 font-mono uppercase tracking-widest">
            Lomond Plant Ltd — Operator Academy
          </p>
        </div>
      </div>
    </div>
  );
}
